import { motion } from "framer-motion";
import { BookMarked, HeartHandshake, Users } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { IMAGES } from "@/lib/images";
import { useSettings } from "@/context/SettingsContext";

const PILLARS = [
  {
    icon: BookMarked,
    title: "Literary Works",
    text: "Books and essays on faith, renewal, and the quiet discipline of service.",
  },
  {
    icon: HeartHandshake,
    title: "Ministry & Revivals",
    text: "Gatherings that call communities back to hope, purpose, and one another.",
  },
  {
    icon: Users,
    title: "Civic Leadership",
    text: "Mentoring young leaders and building institutions that outlast their founders.",
  },
];

const TIMELINE = [
  { year: "2005", text: "Accepted his call to ministry, serving his community through outreach ministry." },
  { year: "2006", text: "Began his public ministry, traveling across the United States with powerful deliverance services." },
  { year: "2010", text: "Launched a prison ministry through which more than 500 souls were led to Christ." },
  { year: "2023 – Present", text: "Appointed Assistant Pastor of True Victory Ministries, overseeing the Outreach & Evangelistic Ministry." },
  { year: "2026", text: "Travels as an ambassador of Christ and prophetic intercessor — preaching and teaching the Word of God, breaking chains and generational curses so that God's people are set free, delivered, and filled with the Holy Ghost and fire." },
];

export const About = () => {
  const { content } = useSettings();
  return (
  <section
    id="about"
    data-testid="about-section"
    className="relative py-24 sm:py-32 bg-parchment dark:bg-ink"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="relative">
              <div className="absolute -inset-3 border border-gold/40 rounded-[2rem] -translate-x-4 translate-y-4" />
              <img
                src={IMAGES.about}
                alt="Portrait of Charles McKuhn"
                className="relative w-full h-[520px] object-cover rounded-[2rem] border border-border"
              />
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal>
            <Overline>About / Biography</Overline>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
              {content?.about_heading ?? "A life yielded to the call of the Holy Ghost."}
            </h2>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-6 space-y-5 text-base sm:text-lg leading-relaxed text-muted-foreground">
              <p>
                {content?.about_p1 ??
                  "Charles A. McKuhn Jr. is an apostle, prophetic intercessor, author, and community leader who flows under the authority and unction of the Holy Ghost. For more than two decades he has preached, written, and interceded around a single conviction: that communities are revived when people answer the call to show up for God and for one another."}
              </p>
              <p>
                {content?.about_p2 ??
                  "Through books, revivals, and keynote talks, Charles invites audiences to rediscover hope, practice servant leadership, and invest in the next generation. His message is practical and warm — grounded in service, and always pointed toward legacy."}
              </p>
            </div>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={0.12 * i}>
                <div className="h-full p-6 rounded-2xl bg-card border border-border hover:-translate-y-1.5 transition-transform duration-300">
                  <p.icon className="text-gold-deep dark:text-gold-light" size={26} />
                  <h3 className="mt-4 text-lg font-semibold">{p.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    {p.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-12">
              <Overline>Milestones</Overline>
              <div className="mt-5 border-l border-border pl-6 space-y-6">
                {TIMELINE.map((t, i) => (
                  <motion.div
                    key={t.year}
                    initial={{ opacity: 0, x: -12 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    className="relative"
                  >
                    <span className="absolute -left-[31px] top-1.5 h-3 w-3 rounded-full bg-gold border-2 border-parchment dark:border-ink" />
                    <div className="font-mono text-sm text-gold-deep dark:text-gold-light">
                      {t.year}
                    </div>
                    <div className="text-sm sm:text-base text-foreground/90">
                      {t.text}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
  );
};
